import { useState } from "react";
import { chakra, type HTMLChakraProps } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";

const GRADIENT_TEXT_STYLE: React.CSSProperties = {
  background: "linear-gradient(135deg, #2cb96a, #1e9e55)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

const ACTIVE_COLOR = "#15803D";

interface StyledNavLinkProps extends Omit<HTMLChakraProps<"a">, "href"> {
  to: string;
  size?: "sm" | "md";
  heroMode?: boolean;
}

const ChakraRouterLink = chakra(RouterLink);

export default function StyledNavLink({
  to,
  children,
  size = "md",
  heroMode = false,
  ...props
}: StyledNavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const [hovered, setHovered] = useState(false);

  // Gradient only on hover when not active and not in hero mode
  const applyGradient = !heroMode && !isActive && hovered;

  const baseColor = (() => {
    if (heroMode) return isActive ? "white" : "whiteAlpha.900";
    if (isActive) return ACTIVE_COLOR;
    return "greyscale.text.title";
  })();

  const underlineColor = heroMode ? "white" : ACTIVE_COLOR;

  return (
    <ChakraRouterLink
      to={to}
      display="inline-block"
      position="relative"
      color={applyGradient ? "transparent" : baseColor}
      fontWeight={isActive ? "800" : "600"}
      textStyle="bodyRegular.base"
      transition="transform 0.25s ease"
      textShadow={heroMode ? "0 2px 8px rgba(0,0,0,0.32)" : "none"}
      style={applyGradient ? GRADIENT_TEXT_STYLE : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      _after={{
        content: '""',
        position: "absolute",
        left: 0,
        bottom: "-4px",
        width: "100%",
        height: size === "sm" ? "1px" : "2px",
        bg: underlineColor,
        borderRadius: "full",
        opacity: isActive ? 1 : 0,
      }}
      _hover={{
        textDecoration: "none",
        transform: "translateY(-2px)",
        ...(heroMode ? { color: "white" } : {}),
      }}
      {...props}
    >
      {children}
    </ChakraRouterLink>
  );
}
